import { messenger } from '@side/next-zendesk-sdk';
import type { RunnerResponseData } from './types';

const UNKNOWN = 'UNKNOWN';
export const DOCUMENT_NOT_ELIGIBLE_FOR_AUTO_TAGGING =
  'DOCUMENT_NOT_ELIGIBLE_FOR_AUTO_TAGGING';
export const DOCUMENT_NOT_ELIGIBLE_MESSAGE =
  'Document is not eligible for auto-tagging';
const DOCUSIGN_ERROR_MESSAGE =
  'Envelope failed to send. For help resolving this issue, please contact side chat support with this code:';

const GENERAL_ERROR =
  'We encountered an error. For help resolving this issue, please contact side chat support with this code:';
const runnerErrorMapper: Record<
  string,
  {
    message: string;
    openMessenger: boolean;
    includeCode: boolean;
  }
> = {
  DOWNLOAD_DOCUMENT: {
    message: 'Document failed to download, try again.',
    openMessenger: true,
    includeCode: true,
  },
  DOWNLOAD_ALL_DOCUMENTS_BY_STATUS: {
    message: 'Documents failed to download, try again.',
    openMessenger: true,
    includeCode: true,
  },
  DOWNLOAD_DOCUSIGN_ENVELOPE: {
    message: 'Documents failed to download, try again.',
    openMessenger: true,
    includeCode: true,
  },
  CREATE_DOCUSIGN_ENVELOPE: {
    message: 'Submit to Docusign failed. Please contact Side Support.',
    openMessenger: true,
    includeCode: true,
  },
  [UNKNOWN]: {
    message: 'Unknown error. Please contact Side Support',
    openMessenger: true,
    includeCode: true,
  },
  DOCUSIGN_USER_NOT_FOUND: {
    message: DOCUSIGN_ERROR_MESSAGE,
    openMessenger: true,
    includeCode: true,
  },
  MAX_EMAIL_BODY_LENGTH_EXCEEDED: {
    message:
      'The message body is too long. Please shorten the message and try again.',
    openMessenger: false,
    includeCode: false,
  },
  DOCUSIGN_USER_LACKS_MEMBERSHIP: {
    message: DOCUSIGN_ERROR_MESSAGE,
    openMessenger: true,
    includeCode: true,
  },
  DOCUSIGN_USER_LACKS_PERMISSIONS: {
    message: DOCUSIGN_ERROR_MESSAGE,
    openMessenger: true,
    includeCode: true,
  },
  DOCUSIGN_USER_AWAITING_ACTIVATION: {
    message: DOCUSIGN_ERROR_MESSAGE,
    openMessenger: true,
    includeCode: true,
  },
  DOCUSIGN_CONSENT_REQUIRED: {
    message: DOCUSIGN_ERROR_MESSAGE,
    openMessenger: true,
    includeCode: true,
  },
  PDF_VALIDATION_FAILED: {
    message: GENERAL_ERROR,
    openMessenger: true,
    includeCode: true,
  },
  INVALID_USERNAME_FOR_RECIPIENT: {
    message: GENERAL_ERROR,
    openMessenger: true,
    includeCode: true,
  },
  RECIPIENTS_NOT_PROVIDED: {
    message: GENERAL_ERROR,
    openMessenger: true,
    includeCode: true,
  },
  TRS_ERROR: { message: GENERAL_ERROR, openMessenger: true, includeCode: true },
  [DOCUMENT_NOT_ELIGIBLE_FOR_AUTO_TAGGING]: {
    message: DOCUMENT_NOT_ELIGIBLE_MESSAGE,
    openMessenger: false,
    includeCode: false,
  },
};

const getRunnerErrorInformation = (errorCode?: string | null) => {
  if (errorCode && errorCode in runnerErrorMapper) {
    return runnerErrorMapper[errorCode];
  }
  return runnerErrorMapper[UNKNOWN];
};

interface RunnerErrorOptions extends ErrorOptions {
  code?: string;
}

export class RunnerError extends Error {
  code?: string;

  constructor(message: string, options?: RunnerErrorOptions) {
    super(message, options);
    this.code = options?.code;
  }
}

export function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

export function getErrorCodeFromResponse(
  response: RunnerResponseData,
): string | undefined {
  if (response && 'error' in response) {
    return response.error.code;
  }
  return undefined;
}

export function handleRunnerError(error: unknown, errorMessage: string): never {
  const code = error instanceof RunnerError ? error.code : undefined;

  if (!code) {
    throw new Error(errorMessage, { cause: error });
  }

  const {
    message: updatedMessage,
    openMessenger,
    includeCode,
  } = getRunnerErrorInformation(code);

  if (openMessenger) {
    if (includeCode) {
      messenger.setErrorCode(code);
    }
    messenger.open();
  }

  // Callers branch on the code, so it has to survive the message mapping.
  throw new RunnerError(`${updatedMessage} ${includeCode ? code : ''}`, {
    code,
    cause: error,
  });
}
